import { useTranslation } from 'react-i18next';
import { log } from '@/lib/logger';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/layout/card';
import { glassCardStyles } from '@/components/ui/layout/card-styles';
import { Badge } from '@/components/ui/badge';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { fetchCalendarToday, fetchTrainingLoad, fetchActivities, fetchActivityStreams } from '@/lib/api';
import { getTodayIntelligence } from '@/lib/intelligence';
import { format } from 'date-fns';
import { Loader2, Moon, Brain, Calendar, TimerOff, Footprints, BedDouble, Zap, CalendarPlus, ChevronDown, ChevronUp, CheckCircle2, Bike, Waves } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { useMemo, useState } from 'react';
import { getTssForDate, enrichActivitiesWithTss, type TrainingLoadData } from '@/lib/tss-utils';
import { SessionCard } from '@/components/sessions/SessionCard';
import { Link, useNavigate } from 'react-router-dom';
import type { CompletedActivity } from '@/types';
import type { TodayResponse } from '@/lib/api';
import { WorkoutDetailCard } from '@/components/workouts/WorkoutDetailCard';
import { ActivityExpandedContent } from '@/components/activities/ActivityExpandedContent';
import { useUnitSystem } from '@/hooks/useUnitSystem';

const sportIcons: Record<string, React.ElementType> = {
  running: Footprints,
  run: Footprints,
  cycling: Bike,
  ride: Bike,
  swimming: Waves,
  swim: Waves,
};

/** Inline expandable card for a completed activity shown on the today page */
function TodayActivityItem({
  activity,
  expanded,
  onToggle,
}: {
  activity: CompletedActivity;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { convertDistance } = useUnitSystem();
  const Icon = sportIcons[activity.sport?.toLowerCase()] || Footprints;

  return (
    <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] backdrop-blur-sm overflow-hidden">
      <button
        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white/[0.025] transition-colors"
        onClick={onToggle}
      >
        <div className="p-1.5 bg-teal-500/10 rounded-md flex-shrink-0">
          <Icon className="h-4 w-4 text-teal-400" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-mono text-slate-200 truncate">
            {activity.title || `${activity.sport} activity`}
          </p>
          <p className="text-[11px] font-mono text-slate-500">
            {activity.duration > 0 && `${activity.duration}min`}
            {activity.duration > 0 && activity.distance > 0 && ' · '}
            {activity.distance > 0 && `${convertDistance(activity.distance).value.toFixed(1)} ${convertDistance(activity.distance).unit}`}
          </p>
        </div>
        <CheckCircle2 className="h-4 w-4 text-teal-400 flex-shrink-0" strokeWidth={1.5} />
        {expanded
          ? <ChevronUp className="h-4 w-4 text-slate-500 flex-shrink-0" />
          : <ChevronDown className="h-4 w-4 text-slate-500 flex-shrink-0" />
        }
      </button>
      {expanded && (
        <div className="border-t border-white/[0.04]">
          <ActivityExpandedContent activity={activity} />
        </div>
      )}
    </div>
  );
}

// Map workout intent to status for badge display
type WorkoutIntent = 'aerobic' | 'threshold' | 'vo2' | 'endurance' | 'recovery';

function mapTypeToIntent(type: string | null | undefined): WorkoutIntent {
  if (!type || typeof type !== 'string') {
    return 'aerobic';
  }
  const lower = type.toLowerCase();
  if (lower.includes('threshold') || lower.includes('tempo')) return 'threshold';
  if (lower.includes('vo2') || lower.includes('interval')) return 'vo2';
  if (lower.includes('endurance') || lower.includes('long')) return 'endurance';
  if (lower.includes('recovery') || lower.includes('easy')) return 'recovery';
  return 'aerobic';
}

interface TodayWorkoutCardProps {
  todayData?: TodayResponse | null;
  isLoading?: boolean;
  error?: unknown;
  trainingLoad7d?: TrainingLoadData | null;
  activities10?: CompletedActivity[] | null;
  todayIntelligence?: unknown;
  /** Additional CSS classes */
  className?: string;
}

export function TodayWorkoutCard(props: TodayWorkoutCardProps = {}) {
  const { t } = useTranslation();
  const today = format(new Date(), 'yyyy-MM-dd');
  const navigate = useNavigate();
  const [showWorkoutDetails, setShowWorkoutDetails] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [expandedActivityId, setExpandedActivityId] = useState<string | null>(null);

  // Use props if provided, otherwise fetch (backward compatibility)
  const propsTodayData = props.todayData;
  const propsIsLoading = props.isLoading;
  const propsError = props.error;
  const propsTrainingLoad7d = props.trainingLoad7d;
  const propsActivities10 = props.activities10;
  const propsTodayIntelligence = props.todayIntelligence;
  const cardClassName = props.className;

  const { data: todayData, isLoading: todayDataLoading, error: todayDataError } = useAuthenticatedQuery({
    queryKey: ['calendarToday', today],
    queryFn: () => fetchCalendarToday(today),
    retry: 1,
    enabled: propsTodayData === undefined,
  });

  const { data: trainingLoadData } = useAuthenticatedQuery<TrainingLoadData>({
    queryKey: ['trainingLoad', 7],
    queryFn: () => {
      log.debug('DASHBOARD', 'Fetching training load for 7 days');
      return fetchTrainingLoad(7);
    },
    retry: (failureCount, error) => {
      if (error && typeof error === 'object') {
        const apiError = error as { code?: string; message?: string; status?: number };
        if (apiError.status === 500 || apiError.status === 503 ||
            apiError.code === 'ECONNABORTED' || 
            (apiError.message && apiError.message.includes('timed out'))) {
          return false;
        }
      }
      return failureCount < 1;
    },
    enabled: propsTrainingLoad7d === undefined,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const { data: activities } = useAuthenticatedQuery({
    queryKey: ['activities', 'limit', 10],
    queryFn: () => fetchActivities({ limit: 10 }),
    retry: 1,
    enabled: propsActivities10 === undefined,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const { data: todayIntelligence } = useAuthenticatedQuery({
    queryKey: ['intelligence', 'today', today],
    queryFn: () => getTodayIntelligence(today),
    retry: 1,
    enabled: propsTodayIntelligence === undefined,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  // Use props if provided, otherwise use fetched data
  const finalTodayData = propsTodayData !== undefined ? propsTodayData : todayData;
  const isLoading = propsIsLoading !== undefined ? propsIsLoading : todayDataLoading;
  const error = propsError !== undefined ? propsError : todayDataError;
  const finalTrainingLoadData = propsTrainingLoad7d !== undefined ? propsTrainingLoad7d : trainingLoadData;
  const finalActivities = propsActivities10 !== undefined ? propsActivities10 : activities;
  const finalTodayIntelligence = propsTodayIntelligence !== undefined ? propsTodayIntelligence : todayIntelligence;

  const todayWorkout = finalTodayData?.sessions?.find(s => s.status === 'planned' || s.status === 'completed') || null;

  const matchingActivity = useMemo(() => {
    if (!todayWorkout || !finalActivities) return null;
    if (todayWorkout.completed_activity_id) {
      return finalActivities.find(activity => activity.id === todayWorkout.completed_activity_id) || null;
    }
    const byDate = finalActivities.find(activity => {
      const activityDate = activity.date?.split('T')[0] || activity.date;
      return activityDate === today;
    });
    return byDate || null;
  }, [finalActivities, today, todayWorkout]);

  const activityId = todayWorkout?.completed_activity_id || matchingActivity?.id || null;

  // Today's completed activities (duration > 0 = actually executed), excluding the one matched to the planned session
  const todayActivities = useMemo(() => {
    if (!finalActivities) return [];
    return finalActivities.filter(activity => {
      if ((activity.duration ?? 0) === 0) return false;
      const activityDate = activity.date?.split('T')[0];
      if (activityDate !== today) return false;
      if (matchingActivity && activity.id === matchingActivity.id) return false;
      return true;
    });
  }, [finalActivities, today, matchingActivity]);

  const activityHasStreams = Boolean(activityId) && matchingActivity?.source !== 'manual';
  const { data: activityStreams } = useAuthenticatedQuery({
    queryKey: ['activityStreams', activityId],
    queryFn: () => fetchActivityStreams(activityId as string),
    retry: 1,
    enabled: activityHasStreams,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Phase 4: SessionCard handles visual representation directly from CalendarSession

  // Check if verdict/decision suggests modification (REST, MODIFY, or CANCEL)
  // MUST be called before any early returns to follow Rules of Hooks
  const shouldSuggestModification = useMemo(() => {
    if (!finalTodayIntelligence) return false;
    
    // Check for v2 format
    if (typeof finalTodayIntelligence === 'object' && 'version' in finalTodayIntelligence) {
      const v2 = finalTodayIntelligence as { version?: string; decision?: string };
      if (v2.version === 'coach_decision_v2') {
        const decision = v2.decision;
        return decision === 'REST' || decision === 'MODIFY' || decision === 'CANCEL';
      }
    }
    
    // Check for v1 format
    if (typeof finalTodayIntelligence === 'object' && 'recommendation' in finalTodayIntelligence) {
      const rec = (finalTodayIntelligence as { recommendation?: string }).recommendation;
      if (rec) {
        const lower = rec.toLowerCase();
        return lower === 'rest' || lower.includes('modify') || lower.includes('cancel');
      }
    }
    
    return false;
  }, [finalTodayIntelligence]);

  const getModificationSuggestion = useMemo(() => {
    if (!finalTodayIntelligence) return null;

    // Check for v2 format
    if (typeof finalTodayIntelligence === 'object' && 'version' in finalTodayIntelligence) {
      const v2 = finalTodayIntelligence as { version?: string; decision?: string; explanation?: string };
      if (v2.version === 'coach_decision_v2') {
        const decision = v2.decision;
        if (decision === 'REST') {
          return {
            type: 'rest' as const,
            message: t('todayWorkoutCard.modifySuggestRest'),
            explanation: v2.explanation,
          };
        }
        if (decision === 'MODIFY') {
          return {
            type: 'modify' as const,
            message: t('todayWorkoutCard.modifySuggestModify'),
            explanation: v2.explanation,
          };
        }
        if (decision === 'CANCEL') {
          return {
            type: 'cancel' as const,
            message: t('todayWorkoutCard.modifySuggestCancel'),
            explanation: v2.explanation,
          };
        }
      }
    }

    // Check for v1 format
    if (typeof finalTodayIntelligence === 'object' && 'recommendation' in finalTodayIntelligence) {
      const rec = (finalTodayIntelligence as { recommendation?: string; explanation?: string }).recommendation;
      if (rec) {
        const lower = rec.toLowerCase();
        if (lower === 'rest') {
          return {
            type: 'rest' as const,
            message: t('todayWorkoutCard.modifySuggestRestV1'),
            explanation: (finalTodayIntelligence as { explanation?: string }).explanation,
          };
        }
      }
    }

    return null;
  }, [finalTodayIntelligence, t]);

  const isCoachPrescribedRest = useMemo(() => {
    if (!finalTodayIntelligence) return false;
    if (typeof finalTodayIntelligence === 'object' && 'version' in finalTodayIntelligence) {
      const v2 = finalTodayIntelligence as { version?: string; decision?: string };
      if (v2.version === 'coach_decision_v2') return v2.decision === 'REST';
    }
    if (typeof finalTodayIntelligence === 'object' && 'recommendation' in finalTodayIntelligence) {
      const rec = (finalTodayIntelligence as { recommendation?: string }).recommendation;
      if (rec) return rec.toLowerCase() === 'rest';
    }
    return false;
  }, [finalTodayIntelligence]);

  const isHighReadiness = useMemo(() => {
    if (!finalTodayIntelligence) return false;
    if (typeof finalTodayIntelligence === 'object' && 'version' in finalTodayIntelligence) {
      const v2 = finalTodayIntelligence as { version?: string; decision?: string };
      if (v2.version === 'coach_decision_v2') return v2.decision === 'PROCEED';
    }
    if (typeof finalTodayIntelligence === 'object' && 'recommendation' in finalTodayIntelligence) {
      const rec = (finalTodayIntelligence as { recommendation?: string }).recommendation;
      if (rec) return rec.toLowerCase() === 'proceed';
    }
    return false;
  }, [finalTodayIntelligence]);

  if (isLoading) {
    return (
      <Card className={cn('h-full flex flex-col', glassCardStyles, cardClassName)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{t('todayWorkoutCard.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const handleAddSessionRequest = () => {
    navigate('/coach', {
      state: {
        draft_message: t('todayWorkoutCard.coachDraftMessage'),
      },
    });
  };

  // Detect if there's no planned session (rest day)
  const hasNoPlannedSession = !error && (!finalTodayData?.sessions ||
    finalTodayData.sessions.filter(s => s.status === 'planned' || s.status === 'completed').length === 0);

  if (error) {
    return (
      <Card className={cn('h-full flex flex-col', glassCardStyles, cardClassName)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{t('todayWorkoutCard.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center py-4">
          <p className="text-sm text-muted-foreground">
            {t('todayWorkoutCard.unableToLoad')}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (hasNoPlannedSession) {
    const btnStyles = "text-[10px] font-mono uppercase tracking-wider border-white/[0.08] text-slate-400 hover:text-slate-200";

    // --- Coach-prescribed rest: show intentional REST DAY with recovery detail ---
    if (isCoachPrescribedRest) {
      const recoverySignals = [
        { label: t('todayWorkoutCard.recoverySignals.trainingLoad'), status: 'optimal' as const, detail: t('todayWorkoutCard.recoverySignals.trainingLoadDetail') },
        { label: t('todayWorkoutCard.recoverySignals.fatigueIndex'), status: 'elevated' as const, detail: t('todayWorkoutCard.recoverySignals.fatigueIndexDetail') },
        { label: t('todayWorkoutCard.recoverySignals.formTsb'), status: 'low' as const, detail: t('todayWorkoutCard.recoverySignals.formTsbDetail') },
        { label: t('todayWorkoutCard.recoverySignals.sleepQuality'), status: 'good' as const, detail: t('todayWorkoutCard.recoverySignals.sleepQualityDetail') },
        { label: t('todayWorkoutCard.recoverySignals.muscleReadiness'), status: 'recovering' as const, detail: t('todayWorkoutCard.recoverySignals.muscleReadinessDetail') },
      ];

      const statusColor = (s: string) => {
        if (s === 'optimal' || s === 'good') return { dot: 'telemetry-dot--green', text: 'text-green-400', glow: 'signal-glow-green' };
        if (s === 'elevated' || s === 'low') return { dot: 'telemetry-dot--amber', text: 'text-amber-400', glow: 'signal-glow-amber' };
        return { dot: 'telemetry-dot--blue', text: 'text-blue-400', glow: 'signal-glow-blue' };
      };

      return (
        <>
          <div className={cn(
            'relative rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm overflow-hidden',
            cardClassName,
          )}>
            <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-amber-500 shadow-[0_0_8px_hsl(35,90%,55%,0.4)]" />
            <div className="p-5 pl-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="telemetry-dot telemetry-dot--amber" />
                <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-500">{t('todayWorkoutCard.sessionLabel')}</span>
              </div>
              <button
                onClick={() => setShowRecoveryModal(true)}
                className="group flex items-center gap-4 mb-4 cursor-pointer bg-transparent border-none p-0 text-left"
              >
                <Moon className="h-6 w-6 text-amber-400 signal-glow-amber" strokeWidth={1.5} />
                <motion.h2
                  className="text-3xl md:text-4xl font-mono font-light tracking-tight text-amber-400 glow-rest select-none"
                  animate={{
                    textShadow: [
                      '0 0 8px hsl(35 90% 55% / 0.4), 0 0 20px hsl(35 90% 55% / 0.15)',
                      '0 0 14px hsl(35 90% 55% / 0.7), 0 0 32px hsl(35 90% 55% / 0.3)',
                      '0 0 8px hsl(35 90% 55% / 0.4), 0 0 20px hsl(35 90% 55% / 0.15)',
                    ],
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                >
                  {t('todayWorkoutCard.restDay')}
                </motion.h2>
                <span className="text-[10px] font-mono text-slate-600 tracking-wider uppercase opacity-0 group-hover:opacity-100 transition-opacity">
                  {t('todayWorkoutCard.tapForDetails')}
                </span>
              </button>
              <div className="space-y-2.5 mb-4">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <TimerOff className="h-4 w-4 text-amber-400/80" strokeWidth={1.5} />
                    <div className="absolute inset-0 blur-[6px] bg-amber-400/20 rounded-full" />
                  </div>
                  <span className="text-[13px] font-mono text-slate-400">{t('todayWorkoutCard.noScheduledTraining')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Footprints className="h-4 w-4 text-emerald-400/80" strokeWidth={1.5} />
                    <div className="absolute inset-0 blur-[6px] bg-emerald-400/20 rounded-full" />
                  </div>
                  <span className="text-[13px] font-mono text-slate-400">{t('todayWorkoutCard.lightMovementOptional')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <BedDouble className="h-4 w-4 text-blue-400/80" strokeWidth={1.5} />
                    <div className="absolute inset-0 blur-[6px] bg-blue-400/20 rounded-full" />
                  </div>
                  <span className="text-[13px] font-mono text-slate-400">{t('todayWorkoutCard.focusOnRecovery')}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className={btnStyles} asChild>
                  <Link to="/coach">
                    <Brain className="h-3.5 w-3.5 mr-1.5" />
                    {t('todayWorkoutCard.recoveryGuidance')}
                  </Link>
                </Button>
                <Button variant="outline" size="sm" className={btnStyles} asChild>
                  <Link to="/schedule">
                    <Calendar className="h-3.5 w-3.5 mr-1.5" />
                    {t('todayWorkoutCard.weekOverview')}
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          <AnimatePresence>
            {showRecoveryModal && (
              <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <motion.div
                  className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                  onClick={() => setShowRecoveryModal(false)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                />
                <motion.div
                  className="relative w-full max-w-lg rounded-lg border border-white/[0.08] bg-slate-950/90 backdrop-blur-xl overflow-hidden shadow-2xl"
                  initial={{ opacity: 0, scale: 0.95, y: 16 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 16 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                >
                  <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-amber-500 shadow-[0_0_12px_hsl(35,90%,55%,0.5)]" />
                  <div className="p-6 pl-7">
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <div className="telemetry-dot telemetry-dot--amber" />
                        <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-500">
                          {t('todayWorkoutCard.recoverySignalsBreakdown')}
                        </span>
                      </div>
                      <button
                        onClick={() => setShowRecoveryModal(false)}
                        className="text-slate-600 hover:text-slate-300 transition-colors text-sm font-mono"
                      >
                        ✕
                      </button>
                    </div>
                    <motion.div
                      className="text-2xl font-mono font-light text-amber-400 glow-rest mb-6"
                      animate={{
                        textShadow: [
                          '0 0 8px hsl(35 90% 55% / 0.4), 0 0 20px hsl(35 90% 55% / 0.15)',
                          '0 0 14px hsl(35 90% 55% / 0.7), 0 0 32px hsl(35 90% 55% / 0.3)',
                          '0 0 8px hsl(35 90% 55% / 0.4), 0 0 20px hsl(35 90% 55% / 0.15)',
                        ],
                      }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      {t('todayWorkoutCard.restDayProtocol')}
                    </motion.div>
                    <div className="space-y-3">
                      {recoverySignals.map((signal, i) => {
                        const colors = statusColor(signal.status);
                        return (
                          <motion.div
                            key={signal.label}
                            className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3"
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.06, duration: 0.2 }}
                          >
                            <div className="flex items-center gap-3 mb-1">
                              <div className={cn('telemetry-dot', colors.dot)} />
                              <span className={cn('text-[11px] font-mono tracking-[0.1em] uppercase', colors.text)}>
                                {signal.label}
                              </span>
                              <span className="ml-auto text-[10px] font-mono text-slate-600 uppercase tracking-wider">
                                {signal.status}
                              </span>
                            </div>
                            <p className="text-[12px] font-mono text-slate-400 leading-relaxed pl-[18px]">
                              {signal.detail}
                            </p>
                          </motion.div>
                        );
                      })}
                    </div>
                    <div className="flex gap-2 mt-5 pt-4 border-t border-white/[0.06]">
                      <Button variant="outline" size="sm" className={btnStyles} asChild>
                        <Link to="/coach" onClick={() => setShowRecoveryModal(false)}>
                          <Brain className="h-3.5 w-3.5 mr-1.5" />
                          {t('todayWorkoutCard.askCoach')}
                        </Link>
                      </Button>
                      <Button variant="outline" size="sm" className={btnStyles} asChild>
                        <Link to="/insights" onClick={() => setShowRecoveryModal(false)}>
                          {t('todayWorkoutCard.viewTrends')}
                        </Link>
                      </Button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      );
    }

    // --- PROCEED verdict + no session: body is primed but nothing scheduled ---
    if (isHighReadiness) {
      return (
        <div className={cn(
          'relative rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm overflow-hidden',
          cardClassName,
        )}>
          <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-green-500 shadow-[0_0_8px_hsl(150,60%,50%,0.4)]" />
          <div className="p-5 pl-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="telemetry-dot telemetry-dot--green" />
              <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-500">{t('todayWorkoutCard.sessionLabel')}</span>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <Zap className="h-6 w-6 text-green-400 signal-glow-green" strokeWidth={1.5} />
              <motion.h2
                className="text-3xl md:text-4xl font-mono font-light tracking-tight text-green-400"
                animate={{
                  textShadow: [
                    '0 0 8px hsl(150 60% 50% / 0.4), 0 0 20px hsl(150 60% 50% / 0.15)',
                    '0 0 14px hsl(150 60% 50% / 0.7), 0 0 32px hsl(150 60% 50% / 0.3)',
                    '0 0 8px hsl(150 60% 50% / 0.4), 0 0 20px hsl(150 60% 50% / 0.15)',
                  ],
                }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              >
                {t('todayWorkoutCard.openSlot')}
              </motion.h2>
            </div>
            <div className="space-y-2.5 mb-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Zap className="h-4 w-4 text-green-400/80" strokeWidth={1.5} />
                  <div className="absolute inset-0 blur-[6px] bg-green-400/20 rounded-full" />
                </div>
                <span className="text-[13px] font-mono text-slate-400">{t('todayWorkoutCard.bodyPrimed')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <CalendarPlus className="h-4 w-4 text-teal-400/80" strokeWidth={1.5} />
                  <div className="absolute inset-0 blur-[6px] bg-teal-400/20 rounded-full" />
                </div>
                <span className="text-[13px] font-mono text-slate-400">{t('todayWorkoutCard.noSessionPlanned')}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" className={btnStyles} onClick={handleAddSessionRequest}>
                <Brain className="h-3.5 w-3.5 mr-1.5" />
                {t('todayWorkoutCard.askCoachToSchedule')}
              </Button>
              <Button variant="outline" size="sm" className={btnStyles} asChild>
                <Link to="/schedule">
                  <Calendar className="h-3.5 w-3.5 mr-1.5" />
                  {t('todayWorkoutCard.weekOverview')}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // --- Default: unstructured gap, no intel verdict ---
    return (
      <div className={cn(
        'relative rounded-lg border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm overflow-hidden',
        cardClassName,
      )}>
        <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-slate-500/50 shadow-[0_0_8px_hsl(215,20%,50%,0.25)]" />
        <div className="p-5 pl-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="telemetry-dot telemetry-dot--blue" />
            <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-500">{t('todayWorkoutCard.sessionLabel')}</span>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <Calendar className="h-6 w-6 text-slate-400" strokeWidth={1.5} />
            <h2 className="text-3xl md:text-4xl font-mono font-light tracking-tight text-slate-400">
              {t('todayWorkoutCard.freeDay')}
            </h2>
          </div>
          <div className="space-y-2.5 mb-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <TimerOff className="h-4 w-4 text-slate-500/80" strokeWidth={1.5} />
                <div className="absolute inset-0 blur-[6px] bg-slate-400/10 rounded-full" />
              </div>
              <span className="text-[13px] font-mono text-slate-500">{t('todayWorkoutCard.noSessionScheduled')}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Footprints className="h-4 w-4 text-emerald-400/80" strokeWidth={1.5} />
                <div className="absolute inset-0 blur-[6px] bg-emerald-400/20 rounded-full" />
              </div>
              <span className="text-[13px] font-mono text-slate-400">{t('todayWorkoutCard.lightMovementOptional')}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className={btnStyles} asChild>
              <Link to="/coach">
                <Brain className="h-3.5 w-3.5 mr-1.5" />
                {t('todayWorkoutCard.askCoach')}
              </Link>
            </Button>
            <Button variant="outline" size="sm" className={btnStyles} asChild>
              <Link to="/schedule">
                <Calendar className="h-3.5 w-3.5 mr-1.5" />
                {t('todayWorkoutCard.weekOverview')}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!todayWorkout) {
    return (
      <Card className={cn('h-full flex flex-col', glassCardStyles, cardClassName)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{t('todayWorkoutCard.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center py-4">
          <p className="text-sm text-muted-foreground">
            {t('todayWorkoutCard.noSessionAvailable')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const workoutType = todayWorkout.type || '';
  const workoutIntent = mapTypeToIntent(workoutType);
  const isCompleted = todayWorkout.status === 'completed' || Boolean(matchingActivity);

  // Map intent to badge variant
  function getIntentVariant(intent: WorkoutIntent): 'default' | 'secondary' | 'destructive' | 'outline' {
    switch (intent) {
      case 'recovery': return 'secondary';
      case 'aerobic': return 'default';
      case 'endurance': return 'default';
      case 'threshold': return 'outline';
      case 'vo2': return 'destructive';
    }
  }

  // Get workout steps from todayWorkout (canonical source)
  const workoutSteps = todayWorkout.steps || [];
  const executionNotes = todayWorkout.execution_notes;
  const mustDos = todayWorkout.must_dos || [];

  // Generate draft message for modification request
  const getGenericDraftMessage = (): string => {
    return "Today was marked as a REST day based on my recovery signals.\nCan you modify today's planned session accordingly (e.g., reduce volume, replace with recovery, or skip)?";
  };

  // Handle navigation to coach with pre-filled message
  const handleModifyRequest = () => {
    const sessionId = todayWorkout.id || null;
    const draftMessage = getGenericDraftMessage();
    
    navigate('/coach', {
      state: {
        context: 'modify_today_session',
        session_id: sessionId,
        suggested_action: 'generic',
        draft_message: draftMessage,
      },
    });
  };

  return (
    <div className={cn('space-y-4', cardClassName)}>
      {/* Coach Suggestion Card - show if verdict suggests modification */}
      {shouldSuggestModification && getModificationSuggestion && (
        <Card className={cn('border-yellow-500/30 bg-yellow-500/5', glassCardStyles)}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
              <Brain className="h-4 w-4" />
              {t('todayWorkoutCard.coachSuggestion')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-foreground mb-2">
              {getModificationSuggestion.message}
            </p>
            {getModificationSuggestion.explanation && (
              <p className="text-xs text-muted-foreground italic">
                {getModificationSuggestion.explanation}
              </p>
            )}
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                onClick={handleModifyRequest}
                size="sm"
                className="bg-coach hover:bg-coach/90 text-coach-foreground"
              >
                {t('todayWorkoutCard.askCoachToModify')}
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/coach">
                  {t('todayWorkoutCard.discussWithCoach')}
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Single collapsible today-session block ── */}
      <div
        className="relative rounded-lg border border-white/[0.08] bg-white/[0.02] backdrop-blur-sm overflow-hidden cursor-pointer hover:bg-white/[0.03] transition-colors"
        onClick={() => setShowWorkoutDetails(prev => !prev)}
      >
        {/* Left accent bar */}
        <div
          className={cn(
            'absolute left-0 top-0 bottom-0 w-[2px]',
            isCompleted
              ? 'bg-teal-500 shadow-[0_0_8px_rgba(0,255,200,0.5)]'
              : 'bg-teal-500/60 shadow-[0_0_8px_rgba(0,255,200,0.25)]',
          )}
        />

        {/* Session summary — always visible */}
        <div className="pl-5 pr-4 pt-4 pb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-500">
              {t('todayWorkoutCard.sessionLabel', 'Session')}
            </span>
            <div className="flex items-center gap-2">
              <Badge variant={isCompleted ? 'default' : getIntentVariant(workoutIntent)}>
                {isCompleted ? t('todayWorkoutCard.completed') : (todayWorkout.intensity || workoutType || t('todayWorkoutCard.planned'))}
              </Badge>
              {showWorkoutDetails
                ? <ChevronUp className="h-4 w-4 text-slate-500" />
                : <ChevronDown className="h-4 w-4 text-slate-500" />
              }
            </div>
          </div>
          <SessionCard
            session={todayWorkout}
            density="standard"
            highlighted={todayWorkout.intensity?.toLowerCase().includes('long') || false}
          />
        </div>

        {/* Expanded detail — stop click from collapsing when interacting inside */}
        {showWorkoutDetails && (
          <div
            className="border-t border-white/[0.04]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Workout steps */}
            <WorkoutDetailCard
              session={todayWorkout}
              expanded
              hideHeader
              hideActions
              onToggleExpand={() => setShowWorkoutDetails(false)}
              onExpandToSchedule={(session) => {
                const sessionDate = session.date || today;
                localStorage.setItem('calendarFocusDate', new Date(sessionDate).toISOString());
                localStorage.setItem('calendarOpenSessionId', session.id);
                navigate('/schedule');
              }}
            />

            {/* Coach insight */}
            {todayWorkout.coach_insight && (
              <div className="px-5 py-3 border-t border-white/[0.04] space-y-2">
                <div className="flex items-center gap-2">
                  <Brain className="h-3.5 w-3.5 text-primary" />
                  <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-slate-500">
                    {t('todayCards.coachInsight', 'Coach Insight')}
                  </span>
                </div>
                <p className="text-[13px] font-mono text-slate-400 leading-relaxed whitespace-pre-wrap">
                  {todayWorkout.coach_insight}
                </p>
              </div>
            )}

            {/* Execution instructions */}
            {(todayWorkout.instructions?.length ?? 0) > 0 && (
              <div className="px-5 py-3 border-t border-white/[0.04] space-y-2">
                <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-slate-500 block">
                  {t('workoutInstructions.title', 'Instructions')}
                </span>
                <ol className="space-y-1.5">
                  {todayWorkout.instructions!.map((instr, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-teal-400 text-[11px] font-mono font-medium mt-0.5">{i + 1}.</span>
                      <span className="text-[13px] font-mono text-slate-400">{instr}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Must-dos */}
            {mustDos.length > 0 && (
              <div className="px-5 py-3 border-t border-white/[0.04] space-y-2">
                <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-slate-500 block">
                  {t('todayWorkoutCard.mustDos', 'Must-dos')}
                </span>
                <ul className="space-y-1.5">
                  {mustDos.map((mustDo, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary text-xs mt-0.5">•</span>
                      <span className="text-[13px] font-mono text-slate-400">{mustDo}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Execution notes (fallback when no must-dos) */}
            {executionNotes && mustDos.length === 0 && (
              <div className="px-5 py-3 border-t border-white/[0.04] space-y-2">
                <span className="text-[10px] font-mono tracking-[0.12em] uppercase text-slate-500 block">
                  {t('todayWorkoutCard.executionNotes', 'Execution Notes')}
                </span>
                <p className="text-[13px] font-mono text-slate-400 leading-relaxed">{executionNotes}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Today's completed activities ── */}
      {todayActivities.length > 0 && (
        <div className="space-y-2">
          <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-500 block px-1">
            {t('todayCards.doneToday', "Done today")}
          </span>
          {todayActivities.map((activity) => (
            <TodayActivityItem
              key={activity.id}
              activity={activity}
              expanded={expandedActivityId === activity.id}
              onToggle={() =>
                setExpandedActivityId(expandedActivityId === activity.id ? null : activity.id)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
